import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { showToast } from "../Toaster";
import "./JobExcelUpload.scss";

interface JobExcelUploadProps {
  onFileProcessed: (data: any[]) => void;
}

const JobExcelUpload: React.FC<JobExcelUploadProps> = ({ onFileProcessed }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadTemplate = () => {
    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Define the template data
    const templateData = [
      {
        // Source Information
        "Source Name": "C&B Electronics Private Limited",
        "Source Latitude": 12.908437,
        "Source Longitude": 77.6509612,

        // Destination Information
        "Destination Name": "Nexus Mall Koramangala",
        "Destination Latitude": 12.9347766,
        "Destination Longitude": 77.6117781,

        // Job Details
        "Job Type": "PICKUP",
        "Job Time": "2024-05-01 12:00:00",
        Status: "INITIATED",

        // Transporter Information
        "Transporter ID": 24,
        "Transporter Name": "Hind Transporter",

        // Trailer Requirements
        "Requested Trailers": 3,
        "Requested 20 Feet Trailers": 1,
        "Requested 40 Feet Trailers": 1,
        "Requested 45 Feet Trailers": 1,

        // Container 1 Details
        "Container 1 Number": "EMCU1111111",
        "Container 1 Size": 20,
        "Container 1 Weight": 4,
        "Container 1 Weight Unit": 0,
        "Container 1 Message": "Test Message 1",

        // Container 2 Details
        "Container 2 Number": "EMCU2222222",
        "Container 2 Size": 40,
        "Container 2 Weight": 5,
        "Container 2 Weight Unit": 0,
        "Container 2 Message": "Test Message 2",

        // Container 3 Details
        "Container 3 Number": "EMCU3333333",
        "Container 3 Size": 45,
        "Container 3 Weight": 6,
        "Container 3 Weight Unit": 0,
        "Container 3 Message": "Test Message 3",

        // Comments
        Comment: "test comment",
      },
    ];

    // Create a worksheet from the template data
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Job Template");

    // Generate Excel file
    XLSX.writeFile(wb, "Job_Creation_Template.xlsx");
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      showToast("Please upload an Excel file (.xlsx or .xls)", "error");
      return;
    }

    setIsProcessing(true);
    try {
      const data = await readExcelFile(file);
      // Transform the Excel data to match the expected JSON structure
      const transformedData = transformExcelData(data[0]);
      onFileProcessed([transformedData]);
      showToast("File processed successfully", "success");
    } catch (error) {
      showToast("Error processing file", "error");
      console.error("Error processing file:", error);
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const transformExcelData = (excelData: any) => {
    // Transform the Excel data to match the expected JSON structure
    return {
      sourceName: excelData["Source Name"],
      sourceLatitude: excelData["Source Latitude"],
      sourceLongitude: excelData["Source Longitude"],
      destinationName: excelData["Destination Name"],
      destinationLatitude: excelData["Destination Latitude"],
      destinationLongitude: excelData["Destination Longitude"],
      jobType: excelData["Job Type"],
      assignedTransporter: {
        id: excelData["Transporter ID"],
        name: excelData["Transporter Name"],
      },
      requestedTrailers: excelData["Requested Trailers"],
      requested20FeetTrailers: excelData["Requested 20 Feet Trailers"],
      requested40FeetTrailers: excelData["Requested 40 Feet Trailers"],
      requested45FeetTrailers: excelData["Requested 45 Feet Trailers"],
      jobContainers: [
        {
          weightUnit: excelData["Container 1 Weight Unit"],
          containerNumber: excelData["Container 1 Number"],
          weight: excelData["Container 1 Weight"],
          size: excelData["Container 1 Size"],
          message: excelData["Container 1 Message"],
        },
        {
          weightUnit: excelData["Container 2 Weight Unit"],
          containerNumber: excelData["Container 2 Number"],
          weight: excelData["Container 2 Weight"],
          size: excelData["Container 2 Size"],
          message: excelData["Container 2 Message"],
        },
        {
          weightUnit: excelData["Container 3 Weight Unit"],
          containerNumber: excelData["Container 3 Number"],
          weight: excelData["Container 3 Weight"],
          size: excelData["Container 3 Size"],
          message: excelData["Container 3 Message"],
        },
      ],
      comments: [
        {
          comment: excelData["Comment"],
        },
      ],
      jobTime: new Date(excelData["Job Time"]).getTime(),
      status: excelData["Status"],
    };
  };

  const readExcelFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      showToast("Please upload an Excel file (.xlsx or .xls)", "error");
      return;
    }

    setIsProcessing(true);
    readExcelFile(file)
      .then((data) => {
        onFileProcessed(data);
        showToast("File processed successfully", "success");
      })
      .catch((error) => {
        showToast("Error processing file", "error");
        console.error("Error processing file:", error);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <div className="job-excel-upload">
      <div className="template-section">
        <button className="template-button" onClick={downloadTemplate}>
          <i className="fas fa-download"></i> Download Template
        </button>
      </div>
      <div
        className={`upload-area ${isProcessing ? "processing" : ""}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        {isProcessing ? (
          <div className="processing-message">Processing file...</div>
        ) : (
          <>
            <div className="upload-icon">
              <i className="fas fa-file-excel"></i>
            </div>
            <div className="upload-text">
              <p>Drag & drop an Excel file here, or click to select one</p>
            </div>
            <div className="upload-hint">
              <p>Supported formats: .xlsx, .xls</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobExcelUpload;
